import { useState } from 'react'
import type { InfiniteData, UseInfiniteQueryResult } from '@tanstack/react-query'
import { useInfiniteScroll } from '@/shared/composables/useInfiniteScroll'
import { ScrollTopButtonMobile } from '@/shared/ui/ScrollTopButton'
import { Link } from 'react-router-dom'
import { QueryState } from '@/shared/ui/QueryState'
import { EmptyStatePage } from '@/shared/ui/EmptyState'
import { Tap } from '@/shared/ui/Tap'
import type { Paged, ProblemCard } from '@/shared/lib/apiTypes'
import { DefaultLayout } from '@/shared/ui/DefaultLayout'
import { countsById } from '@/shared/lib/cardCounts'
import { useCardLabels } from '@/shared/lib/cardLabels'
import { ProblemIssueCard, ProblemListCard } from '@/shared/ui/IssueCard'
import { IssueIcon } from '@/public/features/home/ui/HomeVariants'
import { formatDate } from '@/shared/lib/format'
import { TARGET_LIFECYCLE, TARGET_SITUATION, useCommonCodes, useDongs } from '@/shared/lib/codes'
import {
  EMPTY_FILTER,
  ProblemFilterSheet,
  filterCount,
  type FilterValue,
} from '@/public/features/problem/ui/ProblemFilterSheet'
import { FilterIcon } from '@/shared/ui/icons/FilterIcon'
import { CARD_CATEGORY_MAX } from '@/shared/constants/cards'
import { RECENT_COUNT } from '@/public/features/problem/constants/list'

let lastSeenCount = RECENT_COUNT

export function ProblemListMobile({ query }: { query: UseInfiniteQueryResult<InfiniteData<Paged<ProblemCard>>> }) {
  const { categoryLabel, isReady } = useCardLabels()
  const groups = useCommonCodes('category_group')
  const categories = useCommonCodes('category')
  const lifecycle = useCommonCodes(TARGET_LIFECYCLE)
  const situation = useCommonCodes(TARGET_SITUATION)
  const dongs = useDongs()

  const [group, setGroup] = useState<string | null>(null)
  const [filter, setFilter] = useState<FilterValue>(EMPTY_FILTER)
  const [sheetOpen, setSheetOpen] = useState(false)
  const hasFilter = filterCount(filter) > 0

  const parentOf = new Map((categories.data ?? []).map((c) => [c.code, c.parentCode]))
  const endRef = useInfiniteScroll(
    () => void query.fetchNextPage(),
    query.hasNextPage && !query.isFetchingNextPage,
  )

  const all = query.data?.pages.flatMap((page) => page.items) ?? []
  const byGroup =
    group === null || hasFilter
      ? all
      : all.filter((p) => (p.categoryCodes ?? []).some((c) => parentOf.get(c) === group))

  const items = byGroup.filter((p) => {
    const okCategory =
      filter.categories.length === 0 ||
      (p.categoryCodes ?? []).some((c) => filter.categories.includes(c))
    const okTarget =
      filter.targets.length === 0 || p.targetCodes.some((c) => filter.targets.includes(c))
    const okRegion = filter.regions.length === 0 || filter.regions.includes(p.region.code)
    return okCategory && okTarget && okRegion
  })
  if (items.length > 0) lastSeenCount = items.length

  const counts = countsById(all, (p) => p.matchedSolutionCount)

  const sections = (mode: 'skeleton' | 'data') => (
    <>
      <section className="flex flex-col gap-2.5 p-4 md:mx-auto md:max-w-[var(--list-w)] md:px-[var(--list-pad)] md:pt-[34px] md:pb-[30px]">
        <h2 className="m-0 text-body-lg leading-[1.4] font-semibold text-black-600">최근 등록된 시민 문제</h2>
        <ul className="m-0 -mx-4 flex list-none snap-x snap-mandatory gap-3 overflow-x-auto px-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden [&>li]:snap-start md:grid md:grid-cols-[repeat(var(--list-cols),minmax(0,1fr))] md:gap-[var(--list-gap)] md:overflow-visible md:m-0 md:px-0 md:[&>li]:w-auto md:[&>li>.list-card]:flex md:[&>li>.list-card]:min-h-[140px] md:[&>li>.list-card]:w-auto md:[&>li>.list-card]:flex-col md:[&>li>.list-card]:justify-between md:[&>li>.list-card]:rounded-[14px] md:[&>li>.list-card]:border md:[&>li>.list-card]:border-white-200 md:[&>li>.list-card]:bg-white-50 md:[&>li>.list-card]:px-5 md:[&>li>.list-card]:py-4 md:[&>li>.list-card]:shadow-[0_0_4px_-1px_rgb(0_0_0/6%),0_0_12px_-4px_rgb(0_0_0/12%)]">
          {mode === 'skeleton' ? (
            <li>
              <span className="list-card--reserve list-card--reserve-box" />
            </li>
          ) : (
            all.slice(0, RECENT_COUNT).map((p, i) => {
              const count = counts.get(p.id)
              return (
                <li key={p.id} className="w-[300px] shrink-0">
                  <Link className="flex no-underline" to={`/problems/${p.id}`}>
                    <ProblemIssueCard
                      categories={(p.categoryCodes ?? [])
                        .slice(0, CARD_CATEGORY_MAX)
                        .map(categoryLabel)}
                      title={p.title}
                      summary={p.summary ?? undefined}
                      icon={<IssueIcon index={i} />}
                      metaItems={[formatDate(p.createdAt), p.region.name]}
                      {...(count !== undefined ? { actionText: `해결카드 ${count}장`, actionMuted: count === 0 } : {})}
                    />
                  </Link>
                </li>
              )
            })
          )}
        </ul>
      </section>

      <section className="flex flex-col gap-3 p-4 md:mx-auto md:max-w-[var(--list-w)] md:px-[var(--list-pad)] md:pt-[34px] md:pb-[30px]">
        <div className="relative sticky top-[var(--sticky-top,0px)] z-20 before:absolute before:inset-y-0 before:left-1/2 before:-z-10 before:w-screen before:-translate-x-1/2 before:bg-white-50 flex items-center gap-2 py-2">
          <button
            type="button"
            className={
              hasFilter
                ? 'ui-pressable ui-pressable--circle inline-flex h-[38px] w-[38px] shrink-0 cursor-pointer items-center justify-center rounded-[99px] border-0 p-0 bg-violet-500 text-violet-50'
                : 'ui-pressable ui-pressable--circle inline-flex h-[38px] w-[38px] shrink-0 cursor-pointer items-center justify-center rounded-[99px] border-0 p-0 bg-violet-50 text-black-200'
            }
            aria-label="필터"
            aria-expanded={sheetOpen}
            onClick={() => setSheetOpen(true)}
          >
            <FilterIcon />
          </button>
          <div className="flex min-w-0 gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden md:flex-wrap md:gap-y-2 md:overflow-x-visible" role="group" aria-label="분야">
            <Tap
              selected={group === null && !hasFilter}
              onClick={() => {
                setGroup(null)
                setFilter(EMPTY_FILTER)
              }}
            >
              전체
            </Tap>
            {(groups.data ?? []).map((g) => (
              <Tap
                key={g.code}
                selected={group === g.code && !hasFilter}
                onClick={() => {
                  setGroup(g.code)
                  setFilter(EMPTY_FILTER)
                }}
              >
                {g.label}
              </Tap>
            ))}
          </div>
        </div>
        <ul className="m-0 flex list-none flex-col gap-4 p-0 md:grid md:grid-cols-[repeat(var(--list-cols),minmax(0,1fr))] md:gap-[var(--list-gap)] md:overflow-visible md:[&_.list-card]:flex md:[&_.list-card]:min-h-[140px] md:[&_.list-card]:w-auto md:[&_.list-card]:flex-col md:[&_.list-card]:justify-between md:[&_.list-card]:rounded-[14px] md:[&_.list-card]:border md:[&_.list-card]:border-white-200 md:[&_.list-card]:bg-white-50 md:[&_.list-card]:px-5 md:[&_.list-card]:py-4 md:[&_.list-card]:shadow-[0_0_4px_-1px_rgb(0_0_0/6%),0_0_12px_-4px_rgb(0_0_0/12%)]">
          {mode === 'skeleton'
            ? // 구분선은 **카드 사이**를 가르는 것이다 — 카드가 없는데 선만 떠 있으면 안 된다.
              // 자리만 비워 두고 눈에 보이는 것은 두지 않는다.
              Array.from({ length: lastSeenCount }, (_, i) => (
                <li key={i} className="flex flex-col gap-4 md:w-auto md:[&>.ui-divider-h]:hidden">
                  <span className="list-card--reserve" />
                </li>
              ))
            : items.map((p) => {
                const count = counts.get(p.id)
                return (
                  <li key={p.id} className="md:w-auto">
                    <Link className="flex no-underline" to={`/problems/${p.id}`}>
                      <ProblemListCard
                        categories={(p.categoryCodes ?? [])
                          .slice(0, CARD_CATEGORY_MAX)
                          .map(categoryLabel)}
                        title={p.title}
                        summary={p.summary ?? undefined}
                        metaItems={[formatDate(p.createdAt), p.region.name]}
                        {...(count !== undefined ? { actionText: `해결카드 ${count}장`, actionMuted: count === 0 } : {})}
                      />
                    </Link>
                  </li>
                )
              })}
        </ul>
      </section>
    </>
  )

  return (
    <DefaultLayout className="pc:hidden [--list-w:833px] [--list-pad:34px] [--list-gap:18px] [--list-cols:2] pc:[--list-w:1120px] pc:[--list-cols:3]">

      <QueryState
        isPending={query.isPending || !isReady}
        error={query.error}
        isEmpty={all.length === 0}
        empty={
          <EmptyStatePage
            illust="/illust/empty-problem-large.png"
            title="아직 등록된 시민 문제가 없어요."
            lines={['일상에서 겪는 불편함이나', '해결이 필요한 문제를 알려주세요.']}
            actionText="우리 동네 문제 등록하기"
            actionTo="/problems/new"
          />
        }
        pending={sections('skeleton')}
      >
        {sections('data')}

      <div ref={endRef} aria-hidden="true" />
      {query.isFetchingNextPage && (
        <p className="m-0 py-4 text-center text-body-sm text-black-100">더 불러오는 중…</p>
      )}
      </QueryState>

      {sheetOpen && (
        <ProblemFilterSheet
          value={filter}
          categories={categories.data ?? []}
          lifecycle={lifecycle.data ?? []}
          situation={situation.data ?? []}
          dongs={dongs.data ?? []}
          onApply={(next) => {
            setFilter(next)
            // 필터가 우세다 — 걸리는 순간 대분류 탭 선택은 풀린다.
            if (filterCount(next) > 0) setGroup(null)
            setSheetOpen(false)
          }}
          onClose={() => setSheetOpen(false)}
        />
      )}

      <ScrollTopButtonMobile />
    </DefaultLayout>
  )
}
